import React, { useState } from 'react';
import { Lightbulb, FileText, Target, TrendingUp, Zap, ChevronRight, Sparkles } from 'lucide-react';

const EssayTopicsRedesign = () => {
  const [selectedOption, setSelectedOption] = useState('option1');

  const essayTopics = [
    {
      number: 1,
      title: "The Translation Problem: What Three Discord Sessions Taught Me About Learning",
      description: "Teaching dad to use Discord required three sessions because Alex realized the real problem wasn't the interface—it was that dad thought of communication as sequential (like phone calls) while Discord is simultaneous (like being in a room). By session three, dad was screen sharing better than Alex because he'd learned to think in Discord's native language.",
      angle: "This is actually about cognitive architecture—how Alex learned to diagnose not just what people don't know, but why their existing mental models create barriers to new learning.",
      color: "teal"
    },
    {
      number: 2,
      title: "The Accidental Anthropologist: My Field Study in Human-Phone Interface Design",
      description: "What started as helping grandma with her iPhone became an ongoing research project in how different generations conceptualize technology. Alex has become the family's go-to tech support not because they're the most technical, but because they've learned to speak each person's native metaphor language.",
      angle: "This frames Alex as an unintentional researcher who's conducting longitudinal studies in applied cognitive science every time their phone buzzes with a family tech question.",
      color: "azure"
    },
    {
      number: 3,
      title: "The 300% Solution: How I Accidentally Solved the Wrong Problem (And It Worked)",
      description: "Alex taught themselves video editing to make better robotics clips, but the real breakthrough was realizing their team's engagement problem wasn't about video quality—it was about storytelling. The 300% engagement boost came from showing the human moments behind the robots, not just the technical achievements.",
      angle: "This reveals Alex as someone who starts solving one problem, discovers the real problem is different, and adapts their approach mid-stream rather than stubbornly pursuing their original plan.",
      color: "gold"
    },
    {
      number: 4,
      title: "The Diagnostic Method: Why the Best Tech Support Starts With 'Tell Me What You Expected'",
      description: "Alex has developed their own diagnostic protocol for helping people with technology: instead of asking what went wrong, they ask what the person expected to happen. This shift revealed that most 'tech problems' are actually expectation mismatches, leading to faster, more lasting solutions.",
      angle: "Shows Alex as a methodical thinker who's invented their own framework for a common problem, demonstrating both systematic thinking and human insight.",
      color: "coral"
    }
  ];

    const colorMap = {
      teal: { dot: 'bg-teal-500', text: 'text-teal-700', bg: 'bg-teal-50' },
      azure: { dot: 'bg-azure-500', text: 'text-azure-700', bg: 'bg-azure-50' },
      gold: { dot: 'bg-gold-500', text: 'text-gold-700', bg: 'bg-gold-50' },
      coral: { dot: 'bg-coral-500', text: 'text-coral-700', bg: 'bg-coral-50' }
    };

    return (
      <div className="min-h-screen bg-stone-50 p-8">
      <div className="space-y-8">
        {/* Header with minimal styling */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-slate-900"></div>
            <h2 className="text-base font-bold text-slate-900">Magnetic Hook</h2>
          </div>
          <div className="pl-4 border-l-2 border-slate-900">
            <p className="text-sm text-slate-700 leading-relaxed">
              Alex is a natural translator who bridges gaps others don't even see—between generations, between technical knowledge and everyday understanding, between what people need and what they think they want. They have this rare combination of technical fluency and social intuition that lets them spot communication breakdowns and engineer solutions that actually work for real humans.
            </p>
          </div>
        </div>

        {/* Surprise Insight */}
        <div className="bg-stone-50 border border-stone-300 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full bg-gold-500 mt-2 flex-shrink-0"></div>
            <div>
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">Surprise Insight</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                The most revealing detail isn't that Alex teaches technology—it's that they've accidentally become a student of how different minds work. They're collecting data on human learning patterns without realizing it, developing a methodology for meeting people where they are intellectually. This isn't just helpfulness; it's applied cognitive science.
              </p>
            </div>
          </div>
        </div>

        {/* Essay Topics - Structured Grid */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-slate-900"></div>
            <h3 className="text-sm font-bold text-slate-900">Essay Topics</h3>
          </div>
          
          {/* Header Row */}
          <div className="border-2 border-slate-900 rounded-t-xl bg-slate-900 px-5 py-3">
            <div className="grid grid-cols-12 gap-4 text-xs font-bold text-white uppercase tracking-wider">
              <div className="col-span-1">#</div>
              <div className="col-span-5">Title</div>
              <div className="col-span-6">Strategic Angle</div>
            </div>
          </div>
          
          {/* Topic Rows */}
          <div className="border-2 border-slate-900 border-t-0 rounded-b-xl bg-white divide-y divide-stone-200">
            {essayTopics.map((topic) => {
              const colors = colorMap[topic.color];
              return (
                <div key={topic.number} className="hover:bg-stone-50 transition-colors">
                  <div className="px-5 py-4">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      {/* Number with color dot */}
                      <div className="col-span-1">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${colors.dot}`}></div>
                          <span className="text-sm font-bold text-slate-900">{topic.number}</span>
                        </div>
                      </div>
                      
                      {/* Title */}
                      <div className="col-span-5">
                        <h4 className="text-sm font-bold text-slate-900 leading-tight mb-2">
                          {topic.title}
                        </h4>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {topic.description}
                        </p>
                      </div>
                      
                      {/* Angle */}
                      <div className="col-span-6">
                        <div className={`${colors.bg} rounded-lg p-3 border-l-2 ${colors.dot.replace('bg-', 'border-')}`}>
                          <p className="text-xs text-slate-700 leading-relaxed italic">
                            {topic.angle}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Hidden Strengths */}
        <div className="border-t-2 border-slate-900 pt-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gold-500"></div>
            <h3 className="text-sm font-bold text-slate-900">Hidden Strengths</h3>
          </div>
        </div>
      </div>

      </div>
    );

};

export default EssayTopicsRedesign;